import { canStart } from './canStart';
import { useActions } from '../../hooks';
import ContactModal from './ContactModal';
import { formatDate } from '../../utilities';
import { useAppContext } from '../../shared';
import { useContacts } from '../../templates';
import { ContactName } from '../../templates';
import { tryParseJSON } from '../../utilities';
import { Slug, Button } from '../../components';
import { PaginatePanel } from '../../components';
import { useTableSelection } from '../../templates';
import { ContactsListTemplate } from '../../templates';
import { useContactActions } from './useContactActions';
import { getContactStateColor as getColor } from '../../utilities';
import { addContact, updateContact, deleteContact } from '../../api';
import type { ContactItemType } from '../../templates';

const getStateDetails = (item: ContactItemType) => {
  const result = tryParseJSON(item.latest_contact_state_details || '', true);
  return typeof result === 'object' ? result || {} : { details: result };
};

const Contacts = () => {
  const { meta } = useAppContext();

  const listResult = useContacts();
  const { reloading, refresh } = listResult;

  const {
    selected,
    selectedNodes,
    selectAllElement,

    setSelected,
    renderSelectElement,
  } = useTableSelection({ nodes: listResult.nodes });
  const idsForStart = selectedNodes.filter(canStart).map(node => node.id);

  const {
    element,
    isOpened,
    updating,
    initialValues,

    handleClose,
    handleStartAdd,
    handleAdd,
    handleEdit,
    handleStartEdit,
    handleStartDelete,
  } = useActions<ContactItemType, ContactItemType, ContactItemType>({
    name: 'Contact',
    nameKey: 'email',

    onAdd: addContact,
    onEdit: updateContact,
    onDelete: deleteContact,
    onRefresh: refresh,
    deleteConfig: {
      getText: item => {
        return `The user ${item.email} and all related data will be deleted.\nAre you sure?`;
      },
    },
  });

  const {
    progress,

    startElement,
    openStart,

    wipeElement,
    openWipe,

    bulkStartElement,
    openBulkStart,

    stateDetailsElement,
    handleView,

    bulkDeleteElement,
    openBulkDelete,
  } = useContactActions(refresh, setSelected);

  const executing = progress || updating;

  return (
    <>
      {element}
      {wipeElement}
      {startElement}
      {bulkStartElement}
      {stateDetailsElement}
      {bulkDeleteElement}
      {isOpened && (
        <ContactModal
          loading={executing}
          initialValues={initialValues}
          onClose={handleClose}
          onSumbit={initialValues ? handleEdit : handleAdd}
        />
      )}

      <ContactsListTemplate
        listResult={listResult}
        buttons={
          <>
            <Button icon="refresh" loading={reloading} onClick={refresh}>
              Refresh
            </Button>
            <Button icon="plus" onClick={() => handleStartAdd()}>
              Add Contact
            </Button>
            <Button
              icon="delete"
              type="danger"
              loading={executing}
              disabled={!selected.length}
              onClick={() => openBulkDelete(selected)}
            >
              Delete ({selected.length}) Contacts
            </Button>
            <Button
              icon="play"
              loading={executing}
              onClick={() => openBulkStart(idsForStart)}
              disabled={executing || !idsForStart.length}
            >
              Start for ({idsForStart.length}) Contacts
            </Button>
          </>
        }
        columns={[
          {
            header: selectAllElement,
            getValue: contact => renderSelectElement(contact),
          },
          {
            header: 'Contact',
            sortName: 'email',
            cellClassName: 'text-small',
            getValue: contact => (
              <ContactName contact={contact} withMessagesLink />
            ),
          },
          {
            header: 'Campaign',
            getValue: ({ campaign_id: id }) => {
              const campaign = meta.campaigns.find(v => v.value === id);

              return <Slug text={campaign?.label || id} />;
            },
          },
          {
            header: 'State',
            sortName: 'latest_contact_state',
            cellClassName: 'max-w-[15rem]',
            getValue: ({
              latest_contact_state,
              latest_contact_state_label,
              latest_contact_state_details,
            }) => (
              <Slug
                className="max-w-[13rem]"
                color={getColor(latest_contact_state)}
                text={latest_contact_state_label || 'No State'}
                tooltip={latest_contact_state_details || undefined}
              />
            ),
          },
          {
            header: 'Description',
            sortName: 'description',
            cellClassName: 'text-tiny',
            getValue: ({ description }) => description,
          },
          {
            header: 'Created',
            sortName: 'created_at',
            cellClassName: 'text-tiny',
            getValue: ({ created_at }) => formatDate(created_at),
          },
          {
            header: 'Actions',
            align: 'right',
            getValue: item => (
              <div className="flex flex-row gap-x-sm justify-end">
                {item.latest_contact_state_details && (
                  <Button
                    icon="question"
                    tooltip="Last State Details"
                    onClick={() => handleView(getStateDetails(item))}
                  />
                )}
                <Button
                  icon="play"
                  type="primary"
                  tooltip="Start Campaign"
                  disabled={!canStart(item)}
                  onClick={() => openStart(item)}
                />
                <Button
                  icon="edit"
                  tooltip="Edit Contact"
                  onClick={() => handleStartEdit(item)}
                />
                <Button
                  icon="eraser"
                  tooltip="Clear Contact Data"
                  onClick={() => openWipe(item)}
                />
                <Button
                  icon="delete"
                  type="danger"
                  tooltip="Delete Contact"
                  onClick={() => handleStartDelete(item)}
                />
              </div>
            ),
          },
        ]}
      >
        <PaginatePanel
          {...listResult.pagination}
          setPage={listResult.setPage}
        />
      </ContactsListTemplate>
    </>
  );
};

export default Contacts;
